import { Link } from "react-router-dom";
import Footer from "@/components/site/Footer";
import BookingSidebar from "@/components/site/BookingSidebar";
import hero from "@/assets/tour-kampong-khleang-homestay.jpg";

const itinerary = [
  {
    time: "Day 1 · 8:00 AM or 1:30 PM",
    title: "Hotel Pick-up & Drive to Kampong Khleang",
    body:
      "Pick-up from your hotel in Siem Reap and a 1.5-hour drive through countryside and rice fields to the boat pier at Kampong Khleang.",
  },
  {
    time: "Day 1 · Afternoon",
    title: "Canal Village & Towering Stilt Houses",
    body:
      "Boat trip through the canal village, exploring massive stilt houses up to 10–12 metres high, with visits to the local pagoda and school.",
  },
  {
    time: "Day 1 · Community",
    title: "Fishing Families & Floating Farms",
    body:
      "Meet local fishing families, learn about daily life, and discover the floating fish farms and lake communities of Tonle Sap.",
  },
  {
    time: "Day 1 · Sunset",
    title: "Sunset on the Open Lake",
    body:
      "Boat ride out to open Tonle Sap for a spectacular sunset over the water, with golden light and beautiful village reflections.",
  },
  {
    time: "Day 1 · Evening",
    title: "Homestay Check-in & Khmer Dinner",
    body:
      "Check in at your local homestay (stilt house or floating house depending on season), share a traditional Khmer dinner with your host family, and relax in the quiet village atmosphere. Overnight in Kampong Khleang.",
  },
  {
    time: "Day 2 · Sunrise",
    title: "Sunrise & Morning Village Walk",
    body:
      "Wake for sunrise over the village or lake, take a walk through the stilt houses and watch fishermen begin their daily routine.",
  },
  {
    time: "Day 2 · Morning",
    title: "Breakfast & Cultural Interaction",
    body:
      "Breakfast with the local family, optional flooded-forest boat ride (seasonal), and storytelling with villagers about fishing tools and rice farming life.",
  },
  {
    time: "Day 2 · 11:30 AM–12:30 PM",
    title: "Drive Back to Siem Reap",
    body:
      "Late-morning departure, private transfer back to Siem Reap and drop-off at your hotel.",
  },
];

const includes = [
  "English-speaking driver",
  "Private transport",
  "Boat tours in village and lake",
  "1-night homestay accommodation",
  "1 dinner & 1 breakfast",
  "Drinking water",
];

const excludes = [
  "Personal expenses",
  "Extra drinks",
  "Tips for host family (optional)",
];

const essentials = [
  "Hat, sunglasses & sunscreen",
  "Insect repellent",
  "Binoculars",
  "Light, breathable clothing & a change of clothes",
];

const pricing = [
  { group: "1–2 people", price: "$385 – $425" },
  { group: "3–5 people", price: "$468 – $545" },
  { group: "6–10 people", price: "$589 – $725" },
];

const TourKampongKhleangHomestay = () => {
  return (
    <main className="bg-background">
      <header className="relative bg-jungle text-background overflow-hidden">
        <img
          src={hero}
          alt="Kampong Khleang Tonlé Sap homestay — authentic Cambodia floating village tour with French-speaking guide"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/70 via-jungle/50 to-jungle/95" />
        <div className="relative z-10">
          <div className="container flex items-center justify-between py-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-wide">Aṅkor</span>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70">Sojourns</span>
            </Link>
            <Link to="/tours" className="text-[11px] uppercase tracking-[0.3em] border-b border-background/40 pb-1 hover:border-primary hover:text-primary transition-colors">
              ← All Tours
            </Link>
          </div>
          <div className="container pt-20 pb-32 md:pt-28 md:pb-44 max-w-5xl">
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— 2 Days / 1 Night Floating Home Stay</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-balance">
              Kampong Khleang <em className="font-light">Floating Village</em>.
            </h1>
            <p className="mt-10 max-w-2xl text-background/75 font-light text-lg leading-relaxed">
              The largest stilted community on Tonle Sap towering wooden houses, sprawling canals, and an authentic homestay night far from the tourist crowds.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-[11px] uppercase tracking-[0.3em] text-background/80">
              <span>Duration · 2 Days / 1 Night</span>
              <span>Group · 1–10</span>
              <span>Pickup · 8:00 AM or 1:30 PM</span>
            </div>
          </div>
        </div>
      </header>

      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— The Idea</p>
          <h2 className="font-display text-4xl leading-[1.05] mb-10 text-balance md:text-xl">
            Authentic stilt-village life on the great lake.
          </h2>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p>
              Experience one of the largest and most authentic stilted communities on Tonle Sap Lake at Kampong Khleang. Stay overnight in a local homestay, explore towering stilt houses, cruise through flooded forests, and enjoy peaceful rural life away from tourist crowds.
            </p>
            <p>
              Day 1 takes you deep into the canal village to meet fishing families, visit the pagoda and school, and head out for sunset on the open waters of Tonle Sap before settling into your homestay for a Khmer family dinner.
            </p>
            <p>
              Day 2 begins at sunrise with the village waking around you a morning walk among the stilt houses, breakfast with your host family, and an optional flooded-forest boat ride before the leisurely drive back to Siem Reap.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[1fr_420px] gap-x-12 gap-y-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Itinerary</p>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">
                Two days, one night, eight stops.
              </h2>
              <ol className="space-y-px">
                {itinerary.map((s, idx) => (
                  <li
                    key={idx}
                    className="group grid grid-cols-[auto_1fr] md:grid-cols-[200px_1fr] gap-x-4 md:gap-x-12 min-w-0 border-t border-border py-8 md:py-10 transition-colors hover:bg-secondary/40 last:border-b"
                  >
                    <div className="flex flex-col items-start gap-y-2 text-base">
                      <span className="font-display text-primary tabular-nums text-xl">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="uppercase tracking-[0.25em] text-primary text-xs md:text-base">
                        {s.time}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3 transition-colors group-hover:text-primary">
                        {s.title}
                      </h3>
                      <p className="text-muted-foreground font-light leading-relaxed text-lg max-w-2xl">
                        {s.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <BookingSidebar
              pricePerPerson={385}
              tourTitle="2 Days / 1 Night Kampong Khleang Floating Home Stay"
              pricingTiers={[
                { maxPax: 1, price: 385 },
                { maxPax: 2, price: 425 },
                { maxPax: 3, price: 468 },
                { maxPax: 4, price: 499 },
                { maxPax: 5, price: 545 },
                { maxPax: 6, price: 589 },
                { maxPax: 7, price: 624 },
                { maxPax: 8, price: 678 },
                { maxPax: 9, price: 699 },
                { maxPax: 10, price: 725 },
              ]}
            />
          </div>
        </div>
      </section>

      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Pricing</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Private tour price (per group).
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {pricing.map((p) => (
              <div key={p.group} className="bg-background p-8 border border-border">
                <p className="text-[11px] uppercase tracking-[0.3em] text-primary mb-3">{p.group}</p>
                <p className="font-display text-3xl">{p.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— What's Included</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Everything you need on the lake.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            <div>
              <h3 className="font-display text-2xl mb-5">Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {includes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Not Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {excludes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Good to Know</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Plan your visit.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
            <div>
              <h3 className="font-display text-2xl mb-4">Physical Effort</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                No long uphill hikes, but you should be fit enough to walk up to 1 km in hot conditions on flat or uneven ground. Within the capability of most healthy travellers comfortable with tropical climates.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Weather</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Hot and dry weather is expected throughout. Occasional showers are possible. Midday is hot; wetland sites can be humid. The pace is designed so you avoid touring through the worst of the heat.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Food</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Khmer cuisine is similar to most Southeast Asian cooking but rather less spicy. Host families and lodges prepare a wide variety of good local food, with fresh bread widely available and a surprising range of local brews to try.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Bring With You</h3>
              <ul className="space-y-2 font-light text-muted-foreground">
                {essentials.map((it) => (
                  <li key={it}>• {it}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default TourKampongKhleangHomestay;
